import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import {UserJobRoleThunks} from './userJobRoleThunks';
import Table from 'components/table/Table';
import BlueButton from 'components/BlueButton';
import StatusBadge from 'components/StatusBadge';
import {SearchType} from 'types/search';
import {UserJobRole} from 'types/userJobRole';
import {setActiveTab} from 'features/hr/hrSlice';
import {setIsEditing} from './userJobRoleSlice';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import DotsMenu from 'components/DotsMenu';
import PlusIcon from '../../assets/images/icons/PlusIcon';
import useViewerModal from '../../hooks/useViewerModal';
import {DocumentTemplateSolutionThunks} from '../documentTemplateSolution/documentTemplateSolutionThunks';
import {setUserJobRole} from './userJobRoleSlice';
import usePermissions from '../../hooks/usePermissions';
import {useTableSelection} from 'hooks/useTableSelection';
import {useProcessDocuments} from 'hooks/useProcessDocuments';
import WhiteButton from 'components/WhiteButton';
import DownloadIcon from 'assets/images/icons/DownloadIcon';
import DownloadModal from 'components/DownloadModal';

const UserJobRoleTable: React.FC<{openDrawer: () => void}> = ({openDrawer}) => {
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{userId: string}>();
  const userJobRoles = useSelector(
    (state: RootState) => state.userJobRole.userJobRoles,
  );
  const pagy = useSelector((state: RootState) => state.userJobRole.pagy);
  const activeTab = useSelector((state: RootState) => state.hr.activeTab);
  const loading = useSelector(
    (state: RootState) => state.userJobRole.status === 'loading',
  );
  const {checkPermission} = usePermissions();
  const {
    isAllSelected,
    selectedIds: selectedJobRoles,
    handleSelectAll,
    handleItemSelect: handleJobRoleSelect,
  } = useTableSelection<UserJobRole>(userJobRoles);

  const {handleNameClick, AttachmentModal} = useViewerModal<any>({
    openDrawer,
    fetchItemThunk: UserJobRoleThunks.show,
    fetchDocumentTemplateThunk: DocumentTemplateSolutionThunks.index,
  });

  interface VisibleColumnsType {
    documentTemplate: boolean;
    status: boolean;
    createdAt: boolean;
  }

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumnsType>({
    documentTemplate: true,
    status: true,
    createdAt: true,
  });

  const handleNewJobRole = () => {
    dispatch(setUserJobRole(null));
    dispatch(setIsEditing(true));
    openDrawer();
  };

  const searchByOptions = [
    {
      label: 'Document',
      value: 'documentTemplate.name',
      operator: 'like',
    },
    {label: 'Status', value: 'status', operator: '=='},
  ];

  const columnOptions = [
    {
      label: 'Document',
      value: 'documentTemplate.name',
      isVisible: visibleColumns.documentTemplate,
    },
    {label: 'Status', value: 'status', isVisible: visibleColumns.status},
    {
      label: 'Created At',
      value: 'createdAt',
      isVisible: visibleColumns.createdAt,
    },
  ];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    const {userId} = params;

    search.where.user_id = {
      operator: '==',
      value: userId as string,
    };

    await dispatch(
      UserJobRoleThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleDelete = async (id: string) => {
    await dispatch(UserJobRoleThunks.delete(id));

    const {userId} = params;
    const search = {
      where: {
        user_id: {
          operator: '==',
          value: userId as string,
        },
      },
    };

    await dispatch(
      UserJobRoleThunks.index({
        page: 1,
        search,
        sortField: '',
        sortDirection: '',
        rowsPerPage: 10,
      }),
    );
  };

  const selectedRecords = selectedJobRoles
    .map(id => {
      const doc = userJobRoles.find((d: UserJobRole) => d.id === id);
      return doc ? {id: doc.id, documentType: 'Role'} : null;
    })
    .filter(Boolean) as {id: string; documentType: string}[];

  const openViewerModal = (cfg: any) => {
    handleNameClick(cfg);
  };

  const {
    isDownloadModalOpen,
    modalConfig,
    downloadStatus,
    processDocuments,
    setIsDownloadModalOpen,
    setDownloadStatus,
  } = useProcessDocuments(selectedRecords, openViewerModal);

  return (
    <>
      <Table
        tabs={[
          'HR Documents',
          'Certifications',
          'Trainings',
          'Background Documents',
          'Medical Documents',
          'Applications',
          'Roles',
          'Evaluations',
        ]}
        activeTab={activeTab}
        fetchData={fetchData}
        pagy={pagy}
        searchByOptions={searchByOptions}
        searchable={true}
        pageable={true}
        loading={loading}
        columnOptions={columnOptions}
        onColumnToggle={handleColumnToggle}
        showColumnsButton={true}
        setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
        whiteButton={
          <WhiteButton
            onClick={() => {
              if (selectedRecords.length > 0) processDocuments('download');
            }}
            label={''}
            icon={<DownloadIcon />}
          />
        }
        blueButton={
          checkPermission('create', 'UserJobRole') ? (
            <BlueButton
              onClick={handleNewJobRole}
              label={''}
              icon={<PlusIcon />}
              inputId="new-JobRole-button"
            />
          ) : null
        }>
        <thead>
          <tr>
            <th scope="col" className="px-3 pt-3.5 flex justify-center">
              <input
                type="checkbox"
                className="shrink-0 border-stone-300 rounded disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-600 dark:checked:bg-green-500
                dark:checked:border-green-500 dark:focus:ring-offset-neutral-800"
                checked={isAllSelected}
                onChange={e => handleSelectAll(e.target.checked)}
              />
            </th>
            {visibleColumns.documentTemplate && (
              <TableHeader label="Document" sortBy="documentTemplate.name" />
            )}
            {visibleColumns.status && (
              <TableHeader label="Status" sortBy="status" />
            )}
            {visibleColumns.createdAt && (
              <TableHeader label="Created At" sortBy="createdAt" />
            )}
            <TableHeader label="" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
          {userJobRoles?.map((userJobRole: UserJobRole) => {
            const userOptions = [];

            if (checkPermission('delete', 'HR')) {
              userOptions.push({
                label: 'Delete',
                onClick: () => handleDelete(userJobRole.id),
                hoverClass: 'hover:bg-red-100',
              });
            }

            return (
              <TableRow key={userJobRole.id}>
                <td className="pt-3.5 flex justify-center">
                  <input
                    type="checkbox"
                    className="border border-gray-300 rounded text-white disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-600
                    dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                    checked={selectedJobRoles.includes(userJobRole.id)}
                    onChange={e => {
                      e.stopPropagation();
                      handleJobRoleSelect(userJobRole.id, e.target.checked);
                    }}
                  />
                </td>
                {visibleColumns.documentTemplate && (
                  <TableCell>
                    <div className=" relative group">
                      <div className="w-full flex items-center gap-x-3">
                        <div className="flex-grow">
                          {userJobRole.status !== 'Missing' &&
                          checkPermission('update', 'HR') ? (
                            <span
                              className="text-sm cursor-pointer font-medium underline text-cyan-600 dark:text-neutral-200"
                              onClick={() =>
                                handleNameClick({
                                  id: userJobRole.id,
                                  name: userJobRole.name || '',
                                  status: userJobRole.status || 'Unknown',
                                  attachment: userJobRole.attachment,
                                  docType: 'template',
                                  recordType: 'JobRole',
                                })
                              }>
                              {userJobRole.name}
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                              {userJobRole.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                )}
                {visibleColumns.status && (
                  <TableCell>
                    <StatusBadge status={userJobRole.status ?? 'Unknown'} />
                  </TableCell>
                )}
                {visibleColumns.createdAt && (
                  <TableCell>
                    {new Date(userJobRole.createdAt).toLocaleDateString()}
                  </TableCell>
                )}
                <TableCell>
                  <DotsMenu options={userOptions} />
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
      <AttachmentModal />
      {/* Modal de proceso de documentos */}
      {isDownloadModalOpen && (
        <DownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => {
            setIsDownloadModalOpen(false);
            setDownloadStatus('preparing');
          }}
          status={downloadStatus}
          title={modalConfig?.title}
          customMessages={modalConfig?.customMessages}
          waitMessage={modalConfig?.waitMessage}
          buttonText={modalConfig?.buttonText}
        />
      )}
    </>
  );
};

export default UserJobRoleTable;
